import React, { useState, useEffect, useCallback } from 'react'
import { GoogleMap, useJsApiLoader, Marker, Polygon } from '@react-google-maps/api'
import { setDefaults, fromLatLng } from 'react-geocode'
import { Form, Alert, Button } from 'react-bootstrap'
import { toast } from 'react-toastify'

import { getNormalizedValue } from 'utils/ubicacionUtils'
import { GOOGLE_MAPS_API_KEY } from 'config/general'
import './UbicacionSelector.css'

// Configuración por defecto
const defaultCenter = {
	lat: -25.276750711185308,
	lng: -57.63522231951356
}
const defaultZoom = 13
const defaultGetUbicacionOptions = {
	enableHighAccuracy: true,
	timeout: 5000,
	maximumAge: 0
}

const GOOGLE_MAPS_LIBRARIES = ['geometry']

const containerStyle = {
	width: '100%',
	height: '100%'
}

const getPoligonoStyles = (strokeColor = '#0373c5', fillColor = 'lightblue') => ({
	fillOpacity: 0.5,
	strokeOpacity: 1,
	strokeWeight: 2,
	clickable: false,
	draggable: false,
	editable: false,
	geodesic: false,
	zIndex: 1,
	strokeColor,
	fillColor
})

setDefaults({
	key: GOOGLE_MAPS_API_KEY,
	language: 'es'
})

function UbicacionSelector({
	ubicacion,
	onUbicacionChange,
	areas = [],
	disabled = false,
	required = false,
	mapHeight = '400px',
	mapWidth = '100%',
	titulo = 'Seleccionar ubicación',
	direccionLabel = 'Dirección exacta',
	barrioLabel = 'Barrio',
	ciudadLabel = 'Ciudad',
	miUbicacionLabel = 'Usar mi ubicación',
	fueraDeAreaError = 'La ubicación seleccionada está fuera del área de cobertura',
	noSeleccionadaMsg = 'Favor seleccionar una ubicación',
	direccionPlaceholder = 'Ej: Av. España 123',
	barrioPlaceholder = 'Ej: Centro',
	ciudadPlaceholder = 'Ej: Asunción'
}) {
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: GOOGLE_MAPS_API_KEY,
		libraries: GOOGLE_MAPS_LIBRARIES
	})

	const [mapas, setMapas] = useState(null)
	const [mapa, setMapa] = useState(null)
	const [googleMapsEventDispatched, setGoogleMapsEventDispatched] = useState(false)

	// Disparar evento cuando Google Maps esté completamente cargado
	useEffect(() => {
		if (isLoaded && window.google?.maps?.geometry && !googleMapsEventDispatched) {
			// Disparar evento personalizado
			const event = new CustomEvent('google-maps-loaded', {
				detail: {
					loaded: true,
					hasGeometry: true
				}
			})
			window.dispatchEvent(event)

			setGoogleMapsEventDispatched(true)
		}
	}, [isLoaded, googleMapsEventDispatched])

	const getGeoData = useCallback((json) => {
		let datosGeo = {
			direccion: '',
			barrio: '',
			ciudad: '',
			departamento: '',
			pais: ''
		}

		const addressComponents = json.results[0]?.address_components || []
		const formattedAddress = json.results[0]?.formatted_address || ''

		for (const component of addressComponents) {
			const types = component.types
			if (types.includes('route') || types.includes('street_number')) {
				datosGeo.direccion = datosGeo.direccion
					? `${datosGeo.direccion} ${component.long_name}`
					: component.long_name
			} else if (
				types.includes('neighborhood') ||
				types.includes('sublocality') ||
				types.includes('sublocality_level_1')
			) {
				datosGeo.barrio = component.long_name
			} else if (types.includes('locality') || types.includes('postal_town')) {
				datosGeo.ciudad = component.long_name
			} else if (
				types.includes('administrative_area_level_1') ||
				types.includes('administrative_area_level_2')
			) {
				datosGeo.departamento = component.long_name
			} else if (types.includes('country')) {
				datosGeo.pais = component.long_name
			}
		}

		if (!datosGeo.direccion && formattedAddress) {
			datosGeo.direccion = formattedAddress
		}

		return datosGeo
	}, [])

	const onLoad = useCallback(
		(mapa) => {
			setMapa(mapa)
			setMapas(true)
		},
		[]
	)

	const onUnmount = useCallback(() => {
		setMapa(null)
		setMapas(false)
	}, [])

	const verificarCobertura = useCallback(
		(lat, lon) => {
			if (!window.google?.maps?.geometry || !areas?.length) {
				return { esValida: false, areaId: null }
			}

			const latNum = parseFloat(lat)
			const lonNum = parseFloat(lon)

			if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) {
				return { esValida: false, areaId: null }
			}

			const punto = new window.google.maps.LatLng(latNum, lonNum)
			let areaEncontrada = null

			for (const area of areas) {
				const poligono = new window.google.maps.Polygon({
					paths: area.poligono.map(p => ({
						lat: parseFloat(p.lat),
						lng: parseFloat(p.lon)
					}))
				})

				if (window.google.maps.geometry.poly.containsLocation(punto, poligono)) {
					areaEncontrada = area
					break
				}
			}

			return {
				esValida: !!areaEncontrada,
				areaId: areaEncontrada?.id || null
			}
		},
		[areas]
	)

	const onMapClick = useCallback(
		(event) => {
			if (disabled) return

			const lat = event.latLng.lat()
			const lon = event.latLng.lng()

			// Evitar actualización si las coordenadas son muy cercanas
			if (
				ubicacion?.lat &&
				ubicacion?.lon &&
				Math.abs(ubicacion.lat - lat) < 0.0001 &&
				Math.abs(ubicacion.lon - lon) < 0.0001
			) {
				return
			}

			const { esValida, areaId } = verificarCobertura(lat, lon)

			if (!esValida && areas.length > 0) {
				onUbicacionChange({
					...ubicacion,
					lat,
					lon,
					areaId: null
				})
				return
			}

			fromLatLng(lat, lon)
				.then(response => {
					const geoData = getGeoData(response)
					onUbicacionChange({
						...ubicacion,
						lat,
						lon,
						direccion: geoData.direccion || getNormalizedValue(ubicacion?.direccion) || '',
						barrio: geoData.barrio || getNormalizedValue(ubicacion?.barrio) || '',
						ciudad: geoData.ciudad || getNormalizedValue(ubicacion?.ciudad) || '',
						areaId
					})
				})
				.catch(error => {
					console.error('Error en geocodificación:', error)
					onUbicacionChange({
						...ubicacion,
						lat,
						lon,
						direccion: getNormalizedValue(ubicacion?.direccion) || '',
						barrio: getNormalizedValue(ubicacion?.barrio) || '',
						ciudad: getNormalizedValue(ubicacion?.ciudad) || '',
						areaId
					})
				})
		},
		[disabled, verificarCobertura, areas, ubicacion, onUbicacionChange, getGeoData]
	)

	const onUsarMiUbicacion = useCallback(() => {
		if (disabled) return

		if (!navigator.geolocation) {
			toast.error('La geolocalización no está soportada por este navegador')
			return
		}

		navigator.geolocation.getCurrentPosition(
			(posicion) => {
				const lat = posicion.coords.latitude
				const lon = posicion.coords.longitude

				const { esValida, areaId } = verificarCobertura(lat, lon)

				if (!esValida && areas.length > 0) {
					onUbicacionChange({
						...ubicacion,
						lat,
						lon,
						areaId: null
					})
					return
				}

				fromLatLng(lat, lon)
					.then(response => {
						const geoData = getGeoData(response)
						onUbicacionChange({
							...ubicacion,
							lat,
							lon,
							direccion: geoData.direccion || '',
							barrio: geoData.barrio || '',
							ciudad: geoData.ciudad || '',
							areaId
						})

						if (mapa) {
							mapa.panTo({ lat, lng: lon })
						}
					})
					.catch(error => {
						console.error('Error en geocodificación:', error)
						onUbicacionChange({
							...ubicacion,
							lat,
							lon,
							direccion: '',
							barrio: '',
							ciudad: '',
							areaId
						})

						if (mapa) {
							mapa.panTo({ lat, lng: lon })
						}
					})
			},
			(error) => {
				console.error('Error obteniendo ubicación:', error)
				toast.error('No se pudo obtener su ubicación. Por favor, seleccione manualmente.')
			},
			defaultGetUbicacionOptions
		)
	}, [disabled, verificarCobertura, areas, ubicacion, onUbicacionChange, mapa, getGeoData])

	const handleFieldChange = useCallback(
		(field, value) => {
			onUbicacionChange({
				...ubicacion,
				[field]: value
			})
		},
		[ubicacion, onUbicacionChange]
	)

	useEffect(() => {
		// Validar cuando areaId es null O undefined (no ha sido validado aún o necesita revalidarse)
		const necesitaValidacion = ubicacion?.areaId === undefined || ubicacion?.areaId === null

		if (
			isLoaded &&
			areas.length > 0 &&
			ubicacion?.lat &&
			ubicacion?.lon &&
			necesitaValidacion &&
			window.google?.maps?.geometry
		) {
			const { areaId } = verificarCobertura(ubicacion.lat, ubicacion.lon)

			// Solo actualizar si el areaId cambió para evitar loops infinitos
			if (areaId !== ubicacion.areaId) {
				onUbicacionChange({
					...ubicacion,
					areaId: areaId
				})
			}
		}
	}, [isLoaded, areas, ubicacion, verificarCobertura, onUbicacionChange])

	useEffect(() => {
		if (mapa && ubicacion?.lat && ubicacion?.lon) {
			const lat = parseFloat(ubicacion.lat)
			const lon = parseFloat(ubicacion.lon)

			if (Number.isFinite(lat) && Number.isFinite(lon) &&
				(lat !== defaultCenter.lat || lon !== defaultCenter.lng)) {
				mapa.panTo({ lat, lng: lon })
			}
		}
	}, [mapa, ubicacion?.lat, ubicacion?.lon])

	const esFueraDeArea = () => {
		// Solo mostrar error si:
		// 1. Hay coordenadas
		// 2. Hay áreas definidas
		// 3. areaId es explícitamente null (ya se validó y no está en ningún área)
		// NO mostrar error si areaId es undefined (aún no se ha validado)
		if (!ubicacion?.lat || !ubicacion?.lon || !areas?.length) return false
		return ubicacion.areaId === null
	}

	// Función helper para obtener coordenadas válidas como números
	const getValidCoords = useCallback(() => {
		if (!ubicacion?.lat || !ubicacion?.lon) {
			return null
		}

		const lat = parseFloat(ubicacion.lat)
		const lon = parseFloat(ubicacion.lon)

		// Verificar que sean números finitos válidos
		if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
			return null
		}

		return { lat, lng: lon }
	}, [ubicacion?.lat, ubicacion?.lon])

	const validCoords = getValidCoords()

	// Centro inicial: se calcula una sola vez para evitar re-renders del mapa
	const [centroInicial] = useState(() => {
		if (ubicacion?.lat && ubicacion?.lon) {
			const lat = parseFloat(ubicacion.lat)
			const lon = parseFloat(ubicacion.lon)
			if (Number.isFinite(lat) && Number.isFinite(lon)) {
				return { lat, lng: lon }
			}
		}
		return defaultCenter
	})

	return (
		<div className="ubicacion-selector">
			<h6 className="mb-3">{titulo}</h6>

			{!isLoaded && (
				<div className="text-center py-4">
					<i className="fa fa-spinner fa-spin fa-2x"></i>
					<p className="mt-3 text-muted">Cargando mapa...</p>
				</div>
			)}

			{isLoaded && (
				<div className="map-container mb-3" style={{ height: mapHeight, width: mapWidth }}>
					<GoogleMap
						mapContainerStyle={containerStyle}
						center={centroInicial}
						zoom={defaultZoom}
						onLoad={onLoad}
						onUnmount={onUnmount}
						onClick={onMapClick}
						options={{
							disableDefaultUI: false,
							zoomControl: true,
							mapTypeControl: false,
							streetViewControl: false,
							fullscreenControl: false
						}}
					>
						{validCoords && (
							<Marker position={validCoords} />
						)}

						{areas?.map(area => (
							<Polygon
								key={area.id}
								paths={area.poligono.map(p => ({
									lat: parseFloat(p.lat),
									lng: parseFloat(p.lon)
								}))}
								options={getPoligonoStyles(area.color, area.fillColor)}
							/>
						))}
					</GoogleMap>
				</div>
			)}

			<Button
				variant="outline-primary"
				size="sm"
				onClick={onUsarMiUbicacion}
				disabled={disabled || !isLoaded}
				className="mb-3"
			>
				<i className="fa fa-crosshairs me-2"></i>
				{miUbicacionLabel}
			</Button>

			{required && !ubicacion?.lat && (
				<Alert variant="warning" className="mb-3">
					{noSeleccionadaMsg}
				</Alert>
			)}

			{esFueraDeArea() && areas?.length > 0 && (
				<Alert variant="danger" className="mb-3">
					{fueraDeAreaError}
				</Alert>
			)}

			<Form.Group className="mb-3">
				<Form.Label>{direccionLabel}</Form.Label>
				<Form.Control
					type="text"
					value={getNormalizedValue(ubicacion?.direccion) || ''}
					onChange={(e) => handleFieldChange('direccion', e.target.value)}
					disabled={disabled}
					placeholder={direccionPlaceholder}
					required={required}
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>{barrioLabel}</Form.Label>
				<Form.Control
					type="text"
					value={getNormalizedValue(ubicacion?.barrio) || ''}
					onChange={(e) => handleFieldChange('barrio', e.target.value)}
					disabled={disabled}
					placeholder={barrioPlaceholder}
				/>
			</Form.Group>

			<Form.Group className="mb-3">
				<Form.Label>{ciudadLabel}</Form.Label>
				<Form.Control
					type="text"
					value={getNormalizedValue(ubicacion?.ciudad) || ''}
					onChange={(e) => handleFieldChange('ciudad', e.target.value)}
					disabled={disabled}
					placeholder={ciudadPlaceholder}
				/>
			</Form.Group>
		</div>
	)
}

export default UbicacionSelector